import { fintracService } from "@services/apis/Fintrac";
import {
  FINTRAC_REPORT_STATUSES,
  FintracJobRun,
  FintracReport,
  FintracReportStatus,
  UpdateFintracReportStatusRequest,
} from "@services/models/Fintrac/FintracModel";
import { ColDef, RowClickedEvent } from "ag-grid-community";
import { ReactNode, useCallback, useMemo, useRef, useState } from "react";
import {
  FintracHeader,
  FintracTable,
  fintracAccountTypeColumn,
  fintracAmountColumn,
  fintracCountColumn,
  fintracNumberColumn,
  fintracTextColumn,
} from "./fintracChrome";
import { readFintracError } from "./fintracFormat";
import {
  ReportEditorsProvider,
  ReportReferenceInput,
  ReportSaveButton,
  ReportStatusSelect,
} from "./ReportStatusControl";
import { useFintracList } from "./useFintracList";

const ReportsScreen = ({
  job,
  onOpen,
  onBack,
}: {
  job: FintracJobRun;
  onOpen: (report: FintracReport) => void;
  onBack: () => void;
}) => {
  const [statusFilter, setStatusFilter] = useState<
    FintracReportStatus | undefined
  >(undefined);
  const [statuses, setStatuses] = useState<FintracReportStatus[]>([
    ...FINTRAC_REPORT_STATUSES,
  ]);
  const [actionError, setActionError] = useState("");

  const load = useCallback(
    async (page: number, limit: number) => {
      const response = await fintracService.listReports(job.id, {
        page,
        limit,
        status: statusFilter,
      });
      const nextStatuses = response.data.statuses?.length
        ? response.data.statuses
        : FINTRAC_REPORT_STATUSES;
      setStatuses((current) =>
        current.join() === nextStatuses.join() ? current : [...nextStatuses],
      );
      return {
        rows: response.data.reports ?? [],
        pagination: response.data.pagination,
      };
    },
    [job.id, statusFilter],
  );

  const list = useFintracList(`${job.id}:${statusFilter ?? "all"}`, load);
  const listRef = useRef(list);
  listRef.current = list;

  const onSave = useCallback(
    async (report: FintracReport, body: UpdateFintracReportStatusRequest) => {
      setActionError("");
      try {
        const response = await fintracService.updateReportStatus(
          report.id,
          body,
        );
        const nextStatus = response.data.status;
        listRef.current.setRows((current) =>
          current.map((row) => {
            if (row.id !== report.id) return row;
            return {
              ...row,
              status: nextStatus,
              fintracReference:
                response.data.fintracReference !== undefined
                  ? response.data.fintracReference
                  : row.fintracReference,
            };
          }),
        );
        if (nextStatus === "REPORTED") {
          listRef.current.refetch({ quiet: true });
        }
      } catch (error) {
        setActionError(readFintracError(error));
      }
    },
    [],
  );

  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const columnDefs = useMemo<ColDef[]>(() => {
    const controlColumn = (
      colId: string,
      headerName: string,
      width: number,
      render: (report: FintracReport) => ReactNode,
    ): ColDef => ({
      colId,
      headerName,
      width,
      minWidth: width,
      sortable: false,
      filter: false,
      cellClass: "ag-cell-custom fintrac-status-cell",
      cellStyle: {
        display: "flex",
        alignItems: "center",
      },
      suppressKeyboardEvent: () => true,
      cellRenderer: (params) => {
        if (!params.data) return null;
        return render(params.data as FintracReport);
      },
    });

    return [
      fintracTextColumn("reportDate", "Report Date", 112),
      fintracTextColumn("windowStart", "Window Start", 132),
      fintracTextColumn("windowEnd", "Window End", 132),
      fintracNumberColumn("id", "Report Id", 70, "Report ID copied!"),
      fintracNumberColumn("userId", "User Id", 70, "User ID copied!"),
      fintracAccountTypeColumn("accountCategory", "Category", 120),
      fintracAccountTypeColumn("accountType", "Account Type", 120),
      fintracTextColumn(
        "merchantName",
        "Merchant (From KYB)",
        180,
        "Merchant (From KYB) copied!",
      ),
      fintracTextColumn("dba", "DBA", 100),
      fintracTextColumn(
        "authorisedPersonName",
        "Authorised Person (Name)",
        220,
      ),
      fintracTextColumn("emailId", "E-mail", 210, "E-mail copied!"),
      fintracCountColumn(
        "transactionCount",
        "# Transactions",
        90,
        "transactions",
      ),
      fintracAmountColumn("totalUsdAmount", "USD Value", 90),
      fintracAmountColumn("totalCadAmount", "CAD Value", 90),
      fintracAmountColumn("usdCadFxRate", "Rate", 80),
      controlColumn("status", "Status", 168, (report) => (
        <ReportStatusSelect report={report} statuses={statuses} />
      )),
      controlColumn("fintracReference", "Reference", 180, (report) => (
        <ReportReferenceInput report={report} />
      )),
      controlColumn("save", "Save", 108, (report) => (
        <ReportSaveButton report={report} />
      )),
      fintracTextColumn("reportedAt", "Reported On", 162),
    ];
  }, [statuses]);

  const onRowClicked = (event: RowClickedEvent<FintracReport>) => {
    const target = event.event?.target;
    if (target instanceof Element && target.closest("[data-row-control]")) {
      return;
    }
    if (event.data) onOpen(event.data);
  };

  return (
    <ReportEditorsProvider
      onSave={(report, body) => onSaveRef.current(report, body)}
    >
      <div className="flex h-full flex-col overflow-hidden bg-bg-page-body">
        <FintracHeader
          crumbs={[
            { label: "FINTRAC", onClick: onBack },
            { label: `Job ${job.id}` },
          ]}
          detail={`Window ${job.windowStart} – ${job.windowEnd} · USD/CAD ${job.usdCadFxRate}`}
        />
        <div className="flex shrink-0 flex-wrap gap-2 px-[16px] pt-[16px]">
          <StatusChip
            label="All"
            active={statusFilter === undefined}
            onClick={() => {
              setActionError("");
              setStatusFilter(undefined);
            }}
          />
          {statuses.map((status) => (
            <StatusChip
              key={status}
              label={status}
              active={statusFilter === status}
              onClick={() => {
                setActionError("");
                setStatusFilter(status);
              }}
            />
          ))}
        </div>
        <div className="flex min-h-0 flex-1 flex-col px-[16px] pb-[16px] pt-[12px]">
          {actionError && (
            <p className="mb-2 shrink-0 text-[12px] text-text-status-red">
              {actionError}
            </p>
          )}
          <div className="min-h-0 flex-1">
            <FintracTable
              columnDefs={columnDefs}
              rows={list.rows}
              loading={list.loading}
              loadingMore={list.loadingMore}
              error={list.error}
              totalRecords={list.pagination.totalRecords}
              hasMore={list.hasMore}
              onLoadMore={list.loadMore}
              onRowClicked={onRowClicked}
            />
          </div>
        </div>
      </div>
    </ReportEditorsProvider>
  );
};

const StatusChip = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-[100px] border px-[12px] py-[4px] text-[10px] font-medium text-text-base ${
      active
        ? "border-border-button-secondary bg-bg-button-secondary"
        : "border-border-subtle bg-bg-page-body"
    }`}
  >
    {label}
  </button>
);

export default ReportsScreen;
